import React, { useCallback, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Typography,
  Button,
  Box,
  Alert,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  useFetchUsersWithOrganizationsQuery,
  useUpdateUsersActiveStatusMutation,
} from "../../services/profileService";

/**
 * @typedef {Object} UserOrganization
 * @property {number} organization_id
 * @property {string} organization_name
 * @property {string} role
 * @property {boolean} active
 */

/**
 * @typedef {Object} UserWithOrganizations
 * @property {string} email
 * @property {string} name
 * @property {boolean} active
 * @property {UserOrganization[]} organizations
 */

const formatRoleLabel = (role) => {
  if (!role) return "";
  return String(role)
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");
};

const UserOrganizationsView = () => {
  const { user } = useSelector((state) => state.auth);
  const { userEmail: userEmailParam } = useParams();
  const navigate = useNavigate();
  const [togglingOrgId, setTogglingOrgId] = useState(null);
  const [actionFeedback, setActionFeedback] = useState({ type: "", message: "" });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    org: null,
    nextActive: null,
  });

  const decodedEmail = useMemo(() => {
    if (!userEmailParam) return "";
    try {
      return decodeURIComponent(userEmailParam);
    } catch {
      return userEmailParam;
    }
  }, [userEmailParam]);

  const {
    data: usersPayload,
    isLoading,
    isError,
    error,
  } = useFetchUsersWithOrganizationsQuery(undefined, {
    skip: user?.role !== "superadmin",
  });

  const [updateUsersActiveStatus] = useUpdateUsersActiveStatusMutation();

  const users = useMemo(() => {
    const inner = usersPayload?.data;
    if (Array.isArray(inner)) return inner;
    if (Array.isArray(usersPayload)) return usersPayload;
    return [];
  }, [usersPayload]);

  const selectedUser = useMemo(() => {
    if (!decodedEmail) return null;
    const target = decodedEmail.trim().toLowerCase();
    return (
      users.find((u) => String(u.email || "").trim().toLowerCase() === target) ||
      null
    );
  }, [users, decodedEmail]);

  const sortedOrganizations = useMemo(() => {
    const list = selectedUser?.organizations;
    if (!Array.isArray(list)) return [];
    return [...list].sort((a, b) =>
      String(a.organization_name || "").localeCompare(
        String(b.organization_name || ""),
        undefined,
        { sensitivity: "base" }
      )
    );
  }, [selectedUser]);

  const isTogglingStatus = togglingOrgId != null;

  const handleOpenConfirmDialog = useCallback(
    (org) => {
      if (isTogglingStatus) return;
      setConfirmDialog({
        open: true,
        org,
        nextActive: org.active !== true,
      });
    },
    [isTogglingStatus]
  );

  const handleCloseConfirmDialog = useCallback(() => {
    if (isTogglingStatus) return;
    setConfirmDialog({
      open: false,
      org: null,
      nextActive: null,
    });
  }, [isTogglingStatus]);

  const handleConfirmToggleActiveStatus = useCallback(async () => {
    const org = confirmDialog.org;
    const nextActive = confirmDialog.nextActive;
    const email = String(selectedUser?.email || "").trim();
    const organizationId = org?.organization_id;

    if (!email || organizationId == null || nextActive == null || isTogglingStatus) {
      return;
    }

    setActionFeedback({ type: "", message: "" });
    setTogglingOrgId(organizationId);

    try {
      const result = await updateUsersActiveStatus({
        email,
        organization_id: organizationId,
        active: nextActive,
      }).unwrap();
      const responseData = result?.data ?? result;
      const message =
        (responseData && typeof responseData === "object" && responseData.message) ||
        `User ${nextActive ? "active" : "inactive"} in ${org.organization_name || "organization"} successfully.`;

      setActionFeedback({ type: "success", message: String(message) });
      setConfirmDialog({
        open: false,
        org: null,
        nextActive: null,
      });
    } catch (err) {
      const message =
        (err && typeof err === "object" && err.message) ||
        (err && typeof err === "object" && err.data && err.data.message) ||
        "Failed to update status. Please try again.";
      setActionFeedback({ type: "error", message: String(message) });
    } finally {
      setTogglingOrgId(null);
    }
  }, [
    confirmDialog.nextActive,
    confirmDialog.org,
    isTogglingStatus,
    selectedUser?.email,
    updateUsersActiveStatus,
  ]);

  if (user?.role !== "superadmin") {
    return <Navigate to="/view-org" replace />;
  }

  const errorMessage =
    (error && typeof error === "object" && error.message) ||
    (error && typeof error === "object" && error.data && error.data.message) ||
    "Unable to load user data. Please try again.";

  if (isLoading) {
    return (
      <SectionWrap component="section">
        <ContainerWrap>
          <ContentColumn>
            <Typography sx={{ p: 2, textAlign: "center" }}>Loading…</Typography>
          </ContentColumn>
        </ContainerWrap>
      </SectionWrap>
    );
  }

  if (isError) {
    return (
      <SectionWrap component="section">
        <ContainerWrap>
          <ContentColumn>
            <Alert severity="error" sx={{ mb: 2 }}>
              {String(errorMessage)}
            </Alert>
            <CustomBtn variant="contained" onClick={() => navigate("/users")}>
              Back to users
            </CustomBtn>
          </ContentColumn>
        </ContainerWrap>
      </SectionWrap>
    );
  }

  if (!selectedUser) {
    return (
      <SectionWrap component="section">
        <ContainerWrap>
          <ContentColumn>
            <Typography sx={{ mb: 2, textAlign: "center" }}>User not found.</Typography>
            <CustomBtn variant="contained" onClick={() => navigate("/users")}>
              Back to users
            </CustomBtn>
          </ContentColumn>
        </ContainerWrap>
      </SectionWrap>
    );
  }

  return (
    <SectionWrap component="section">
      <ContainerWrap>
        <ContentColumn>
          
          <Box sx={{ textAlign: "center", mb: 2 }}>
              <TitleWrap component="h1">User organizations</TitleWrap>
            </Box>

          {actionFeedback.message ? (
            <Alert severity={actionFeedback.type || "info"} sx={{ mb: 2 }}>
              {actionFeedback.message}
            </Alert>
          ) : null}

          <CardWrap>
            <Box>
              <Typography sx={{ color: "#2C2C2E", mb: 0.5 }} variant="body1">
                <strong>Name:</strong> {selectedUser.name ?? "—"}
              </Typography>
              <Typography sx={{ color: "#2C2C2E" }} variant="body1">
                <strong>Email:</strong> {selectedUser.email ?? "—"}
              </Typography>

              <TitleWrap component="h5">Organizations</TitleWrap>
            </Box>

            {sortedOrganizations.length === 0 ? (
              <Typography color="text.secondary" sx={{ textAlign: "center" }}>
                No organizations for this user.
              </Typography>
            ) : (
              sortedOrganizations.map((org) => {
                const isOrgActive = org.active === true;
                const isOrgToggling = togglingOrgId === org.organization_id;

                return (
                  <OrgBlock
                    key={org.organization_id ?? org.organization_name}
                    $inactive={!isOrgActive}
                  >
                    <OrgInfo>
                      <OrgName variant="subtitle1">{org.organization_name ?? "—"}</OrgName>
                      <RoleText variant="body2">{formatRoleLabel(org.role)}</RoleText>
                      <StatusText variant="body2" $active={isOrgActive}>
                        {isOrgActive ? "Active" : "Inactive"}
                      </StatusText>
                    </OrgInfo>
                    <OrgActionBtn
                      disabled={isTogglingStatus}
                      variant="contained"
                      onClick={() => handleOpenConfirmDialog(org)}
                      sx={
                        isOrgActive
                          ? { background: "#6B7280", borderColor: "#6B7280" }
                          : { background: "#2E7D32", borderColor: "#2E7D32" }
                      }
                    >
                      {isOrgToggling
                        ? "Updating…"
                        : isOrgActive
                          ? "Inactive"
                          : "Active"}
                    </OrgActionBtn>
                  </OrgBlock>
                );
              })
            )}
          </CardWrap>

          <CustomBtn variant="contained" sx={{ mt: 3 }} onClick={() => navigate("/users")}>
            Back
          </CustomBtn>
        </ContentColumn>
      </ContainerWrap>

      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "7px",
            padding: "10px",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "20px", fontWeight: 600, textAlign: "center", pb: 1 }}>
          Confirm status change
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", color: "#2C2C2E" }}>
          {confirmDialog.nextActive === true ? (
            <Typography>
              Activate <strong>{selectedUser.name || selectedUser.email}</strong> in{" "}
              <strong>{confirmDialog.org?.organization_name || "this organization"}</strong>?
            </Typography>
          ) : (
            <Typography>
              Inactive <strong>{selectedUser.name || selectedUser.email}</strong> in{" "}
              <strong>{confirmDialog.org?.organization_name || "this organization"}</strong>?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: "10px", pb: 2 }}>
          <CustomBtn
            variant="outlined"
            disabled={isTogglingStatus}
            onClick={handleCloseConfirmDialog}
            sx={{
              width: "auto",
              background: "#ffffff",
              color: "#2C2C2E",
              borderColor: "#DADADA",
              "&:hover": {
                background: "#f5f5f5",
                borderColor: "#DADADA",
              },
            }}
          >
            Cancel
          </CustomBtn>
          <CustomBtn
            disabled={isTogglingStatus}
            onClick={handleConfirmToggleActiveStatus}
            sx={{
              width: "auto",
              minWidth: "100px",
              ...(confirmDialog.nextActive === true
                ? { background: "#2E7D32", borderColor: "#2E7D32" }
                : { background: "#6B7280", borderColor: "#6B7280" }),
            }}
          >
            {isTogglingStatus ? (
              <CircularProgress size={18} sx={{ color: "#ffffff" }} />
            ) : (
              "Confirm"
            )}
          </CustomBtn>
        </DialogActions>
      </Dialog>
    </SectionWrap>
  );
};

export default UserOrganizationsView;

const SectionWrap = styled(Box)(() => ({
  padding: "30px 0",
}));

const ContainerWrap = styled(Box)(() => ({
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
  marginLeft: "auto",
  marginRight: "auto",
}));

const ContentColumn = styled(Box)(() => ({
  width: "100%",
  maxWidth: "640px",
  marginLeft: "auto",
  marginRight: "auto",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
}));

const TitleWrap = styled(Typography)(() => ({
  fontSize: "28px",
  fontWeight: 600,
  lineHeight: 1.445,
  color: "#000000",
  marginBottom: "12px",
}));

const CardWrap = styled(Box)(() => ({
  width: "100%",
  padding: "24px",
  background: "#ffffff",
  borderRadius: "7px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
}));

const OrgBlock = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$inactive",
})(({ $inactive }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "16px",
  paddingBottom: "16px",
  marginBottom: "16px",
  borderBottom: "1px solid #eeeeee",
  ...($inactive && {
    opacity: 0.65,
  }),
  "&:last-of-type": {
    borderBottom: "none",
    marginBottom: 0,
    paddingBottom: 0,
  },
}));

const OrgInfo = styled(Box)(() => ({
  flex: 1,
  minWidth: 0,
}));

const OrgName = styled(Typography)(() => ({
  fontWeight: 600,
  color: "#2C2C2E",
  marginBottom: "4px",
}));

const RoleText = styled(Typography)(() => ({
  color: "rgba(44, 44, 46, 0.75)",
  paddingLeft: 0,
}));

const StatusText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$active",
})(({ $active }) => ({
  marginTop: "6px",
  fontWeight: 500,
  color: $active ? "#2E7D32" : "#6B7280",
}));

const OrgActionBtn = styled(Button)(() => ({
  flexShrink: 0,
  minWidth: "110px",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: 1.25,
  color: "#ffffff",
  textTransform: "capitalize",
  borderRadius: "8px",
  padding: "8px 14px",
  boxShadow: "none !important",
  "&:hover": {
    boxShadow: "none !important",
  },
  "&:disabled": {
    color: "#ffffff",
    opacity: 0.7,
  },
}));

const CustomBtn = styled(Button)(() => ({
  width: "100%",
  minWidth: "unset",
  fontSize: "18px",
  fontWeight: 500,
  lineHeight: 1.25,
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "12px",
  padding: "14px 24px",
  boxShadow: "none !important",
  "&:hover": {
    background: "#dc731d",
    borderColor: "#dc731d",
    boxShadow: "none !important",
  },
  "&:disabled": {
    background: "rgba(242, 128, 33, 0.45)",
    color: "#ffffff",
    borderColor: "transparent",
  },
}));
